import { BrandfolderHexColors, useDrag } from '@brandfolder/react';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { ColorRender } from '@components/blocks/color/color-render';
import { AsideContext } from '@context/aside';
import { DragAndDropContext } from '@context/drag-and-drop';
import { LocaleContext } from '@context/locale';
import { PageContext } from '@context/page';
import { BlockDragAndDropFormats, BlockTypes } from '@enums/blocks';
import { Asset, AssetDataColor } from '@typings/asset';
import { BlockColor, BlockDragItem } from '@typings/block';
import { createBlockDragItem } from '@utilities/block';

interface ColorsListItemProps extends HTMLAttributes<HTMLLIElement> {
  asset: Asset;
  id: string;
}

const UnstyledColorsListItem: FunctionComponent<ColorsListItemProps> = (props) => {
  const { asset, id, ...otherProps } = props;
  const { data: assetData, description, name } = asset;

  const ref = useRef<HTMLLIElement | null>(null);

  const { setShowBlocks } = useContext(AsideContext);
  const { setDragging } = useContext(DragAndDropContext);
  const { locale } = useContext(LocaleContext);
  const { page } = useContext(PageContext);

  const getData = (): BlockDragItem => {
    const data: BlockColor = {
      data: assetData as AssetDataColor,
      description: description || '',
      key: id,
      name
    };

    return createBlockDragItem({
      data,
      locale,
      pageKey: page?.key as string,
      type: BlockTypes.Color
    });
  };

  const { dragging } = useDrag({
    data: getData(),
    format: BlockDragAndDropFormats.BlockDrag,
    ref
  });

  useEffect(() => {
    setDragging(dragging);
  }, [dragging]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <li
      {...otherProps}
      className={classNames({
        'colors-list-item': true,
        'colors-list-item--dragging': dragging,
        [`${otherProps?.className}`]: !!otherProps?.className
      })}
      ref={ref}
      draggable
      onDragEnd={() => {
        setShowBlocks(false);
      }}
    >
      <ColorRender aside assetData={assetData} id={id} name={name} showCopy={false} />
    </li>
  );
};

const StyledColorsListItem = styled(UnstyledColorsListItem)`
  border-color: transparent;
  border-radius: 8px;
  border-style: dashed;
  border-width: 2px;
  cursor: grab;
  margin: 0;
  padding: 4px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${BrandfolderHexColors.GrayMediumDark};
  }

  &.colors-list-item--dragging {
    background-color: ${BrandfolderHexColors.White};
    opacity: 0.5;
  }
`;

export const ColorsListItem: FunctionComponent<ColorsListItemProps> = (props) => {
  return <StyledColorsListItem {...props} />;
};
